import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Receipt, Upload, Users, TrendingUp, Building2, Home, User, Landmark, RefreshCw, Wallet, Save, Target, ExternalLink, Download, FileUp } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, ComposedChart, ReferenceLine, Area } from "recharts";
import { Slider } from "./ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtEur = (v) => v == null || isNaN(v) ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v) => v == null || isNaN(v) ? "—" : new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 }).format(v);

const SERVICE_COLORS = {
  cleaning: "#3b82f6",
  pest_control: "#ef4444",   // Disinfestazione (rosso)
  rat_control: "#a16207",    // Derattizzazione (marrone/senape)
  gardening: "#10b981",
  snow: "#06b6d4",
  // Studi fisioterapici / centri riabilitazione
  physiotherapy: "#0ea5e9",   // sky
  tecartherapy: "#8b5cf6",    // viola
  laser_therapy: "#ec4899",   // rosa
  shockwave: "#f97316",       // arancione
  posture: "#22c55e",         // verde
  massage: "#14b8a6",         // teal
  other: "#94a3b8",
};
const CUSTOMER_TYPE_LABEL = { condominio: "Condominio", azienda: "Azienda", privato: "Privato", pa: "Pubblica Amministrazione" };
const CUSTOMER_TYPE_ICON = { condominio: Home, azienda: Building2, privato: User, pa: Landmark };
const CUSTOMER_TYPE_COLOR = { condominio: "#8b5cf6", azienda: "#3b82f6", privato: "#10b981", pa: "#f59e0b" };

export const ServiceInvoicesPage = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [year, setYear] = useState(null);
  const [summary, setSummary] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const fileInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Esegui le 2 chiamate in parallelo
      const [sumRes, custRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/service-invoices/summary${year ? `?year=${year}` : ''}`),
        axios.get(`${API}/clients/${clientId}/service-invoices/top-customers${year ? `?year=${year}&limit=1000` : '?limit=1000'}`),
      ]);
      setSummary(sumRes.data);
      if (sumRes.data.available_years?.length && !year) {
        setYear(sumRes.data.available_years[0]);
      }
      setTopCustomers(custRes.data.customers || []);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento dati fatture");
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/clients/${clientId}/service-invoices/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      const { inserted, skipped_duplicates, errors, total_xml, whale_curve_sync } = res.data;
      toast.success(`${inserted} fatture importate (${skipped_duplicates} duplicati, ${errors} errori su ${total_xml} XML)`);
      if (whale_curve_sync && (whale_curve_sync.created || whale_curve_sync.updated)) {
        toast.info(`🐋 Whale Curve sincronizzata: ${whale_curve_sync.created} nuovi clienti, ${whale_curve_sync.updated} aggiornati`);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Eliminare TUTTE le fatture caricate per questo cliente? Operazione irreversibile.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/service-invoices`);
      toast.success("Fatture eliminate");
      await fetchAll();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Caricamento…</div>;

  const labels = summary?.service_labels || {};
  const totalLordo = summary?.summary?.total_lordo || 0;
  const totalNetto = summary?.summary?.total_netto || 0;
  const nInvoices = summary?.summary?.n_invoices || 0;
  const nCustomers = summary?.summary?.n_customers || 0;

  // Monthly chart data: pivot by month with services as series
  const monthlyMap = {};
  (summary?.monthly || []).forEach(m => {
    const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
    if (!monthlyMap[key]) monthlyMap[key] = { _sortKey: key, name: `${MONTHS_IT[m.month - 1]} ${String(m.year).slice(-2)}` };
    monthlyMap[key][labels[m.service_type] || m.service_type] = m.total_netto;
  });
  const monthlyChartData = Object.values(monthlyMap).sort((a, b) => a._sortKey.localeCompare(b._sortKey));
  const monthlyKeys = [...new Set((summary?.monthly || []).map(m => labels[m.service_type] || m.service_type))];

  // Service pie data
  const servicePieData = (summary?.by_service || []).map(s => ({
    name: labels[s.service_type] || s.service_type,
    value: s.total_netto,
    fill: SERVICE_COLORS[s.service_type] || "#94a3b8",
  }));

  // Customer type pie data
  const custPieData = (summary?.by_customer_type || []).map(c => ({
    name: CUSTOMER_TYPE_LABEL[c.customer_type] || c.customer_type,
    value: c.total_netto,
    fill: CUSTOMER_TYPE_COLOR[c.customer_type] || "#94a3b8",
  }));

  return (
    <div className="space-y-6" data-testid="service-invoices-page">
      {/* HEADER + UPLOAD */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-600" />
              Fatture Elettroniche — Analisi Servizi & Clienti
            </CardTitle>
            <div className="flex items-center gap-2">
              {summary?.available_years?.length > 0 && (
                <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-32" data-testid="year-filter"><SelectValue placeholder="Anno" /></SelectTrigger>
                  <SelectContent>
                    {summary.available_years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <input ref={fileInputRef} type="file" accept=".xml,.zip" onChange={handleUpload} style={{ display: "none" }} data-testid="upload-input" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="gap-2" data-testid="upload-btn">
                <Upload className="h-4 w-4" />
                {uploading ? "Caricamento…" : "Carica XML o ZIP"}
              </Button>
              {nInvoices > 0 && (
                <Button variant="outline" size="icon" onClick={handleReset} title="Reset" data-testid="reset-btn">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {nInvoices === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-base mb-2">Nessuna fattura caricata.</p>
              <p className="text-sm">Carica un singolo XML FatturaPA o uno ZIP contenente più XML.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                <div className="text-xs text-blue-700 uppercase tracking-wide font-semibold">Fatture</div>
                <div className="text-2xl font-bold text-blue-900 tabular-nums">{nInvoices}</div>
              </div>
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                <div className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Fatturato Netto</div>
                <div className="text-2xl font-bold text-emerald-900 tabular-nums">{fmtEur(totalNetto)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                <div className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Fatturato Lordo</div>
                <div className="text-2xl font-bold text-amber-900 tabular-nums">{fmtEur(totalLordo)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-violet-50 border-violet-200">
                <div className="text-xs text-violet-700 uppercase tracking-wide font-semibold">Clienti Unici</div>
                <div className="text-2xl font-bold text-violet-900 tabular-nums">{nCustomers}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {nInvoices === 0 ? null : (
        <Tabs defaultValue="services" className="space-y-4">
          <TabsList>
            <TabsTrigger value="services" data-testid="tab-services">📊 Per Servizio</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">👥 Per Cliente</TabsTrigger>
            <TabsTrigger value="trend" data-testid="tab-trend">📈 Trend Mensile</TabsTrigger>
            <TabsTrigger value="margin" data-testid="tab-margin">💰 Margine & Pricing</TabsTrigger>
            <TabsTrigger value="margin_eff" data-testid="tab-margin-eff">📈 Margine Effettivo</TabsTrigger>
            <TabsTrigger value="hourly" data-testid="tab-hourly">⏱️ Costo Orario</TabsTrigger>
          </TabsList>

          {/* TAB: SERVIZI */}
          <TabsContent value="services" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Analisi per Servizio</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                  <div className="lg:col-span-1">
                    <h4 className="text-sm font-semibold mb-2 text-slate-700">Distribuzione fatturato</h4>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={servicePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(d) => `${(d.percent * 100).toFixed(0)}%`}>
                          {servicePieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtEur(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="lg:col-span-2 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="services-table">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left py-2 px-2 font-semibold">Servizio</th>
                          <th className="text-right py-2 px-2">Fatturato</th>
                          <th className="text-right py-2 px-2">% Tot.</th>
                          <th className="text-right py-2 px-2">Quantità</th>
                          <th className="text-right py-2 px-2">€/unità medio</th>
                          <th className="text-right py-2 px-2">Min / Max</th>
                          <th className="text-right py-2 px-2">Clienti</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(summary?.by_service || []).map(s => {
                          const color = SERVICE_COLORS[s.service_type] || "#94a3b8";
                          const pct = totalNetto > 0 ? (s.total_netto / totalNetto * 100) : 0;
                          return (
                            <tr key={s.service_type} className="border-b hover:bg-slate-50">
                              <td className="py-1.5 px-2">
                                <Badge style={{ backgroundColor: color, color: "white" }}>{labels[s.service_type] || s.service_type}</Badge>
                              </td>
                              <td className="text-right tabular-nums font-semibold">{fmtEur(s.total_netto)}</td>
                              <td className="text-right tabular-nums text-muted-foreground">{pct.toFixed(1)}%</td>
                              <td className="text-right tabular-nums">{fmtNum(s.total_qty)}</td>
                              <td className="text-right tabular-nums font-semibold text-violet-700">{fmtEur(s.avg_unit_price)}</td>
                              <td className="text-right tabular-nums text-[11px] text-muted-foreground">{fmtEur(s.min_unit_price)} / {fmtEur(s.max_unit_price)}</td>
                              <td className="text-right tabular-nums">{s.n_customers}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer type breakdown */}
            <Card>
              <CardHeader><CardTitle className="text-base">Distribuzione per Tipo Cliente</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={custPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(d) => `${(d.percent * 100).toFixed(0)}%`}>
                        {custPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtEur(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="lg:col-span-2 grid grid-cols-2 gap-3 content-start">
                    {(summary?.by_customer_type || []).map(c => {
                      const Icon = CUSTOMER_TYPE_ICON[c.customer_type] || Building2;
                      const color = CUSTOMER_TYPE_COLOR[c.customer_type];
                      const pct = totalNetto > 0 ? (c.total_netto / totalNetto * 100) : 0;
                      return (
                        <div key={c.customer_type} className="border rounded-lg p-3" style={{ borderColor: color + "40" }}>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="h-4 w-4" style={{ color }} />
                            <span className="font-semibold text-sm">{CUSTOMER_TYPE_LABEL[c.customer_type]}</span>
                            <span className="ml-auto text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
                          </div>
                          <div className="text-xl font-bold tabular-nums">{fmtEur(c.total_netto)}</div>
                          <div className="text-[11px] text-muted-foreground">{c.n_customers} clienti · {c.n_invoices} fatture</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: CLIENTI */}
          <TabsContent value="customers" className="space-y-4">
            <CustomerWhaleSection
              topCustomers={topCustomers}
              labels={labels}
              clientId={clientId}
              year={year}
              onSynced={fetchAll}
            />
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Top {Math.min(20, topCustomers.length)} Clienti per Fatturato {year || "tutti gli anni"}
                  {topCustomers.length > 20 && (
                    <span className="text-xs font-normal text-muted-foreground ml-2">
                      (su {topCustomers.length} totali — vedi curva Whale sopra)
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm" data-testid="top-customers-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left py-2 px-2 font-semibold">#</th>
                      <th className="text-left py-2 px-2 font-semibold">Cliente</th>
                      <th className="text-left py-2 px-2">Tipo</th>
                      <th className="text-left py-2 px-2">Comune</th>
                      <th className="text-right py-2 px-2">Fatturato</th>
                      <th className="text-right py-2 px-2">Fatture</th>
                      <th className="text-right py-2 px-2">€/fattura</th>
                      <th className="text-left py-2 px-2">Mix Servizi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCustomers.slice(0, 20).map((c, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="py-1.5 px-2 text-muted-foreground tabular-nums">{i + 1}</td>
                        <td className="py-1.5 px-2 font-medium">{c.customer_denominazione || "—"}<div className="text-[10px] text-muted-foreground">{c.customer_piva || c.customer_cf}</div></td>
                        <td className="py-1.5 px-2"><Badge variant="outline" className="text-[10px]">{CUSTOMER_TYPE_LABEL[c.customer_type]}</Badge></td>
                        <td className="py-1.5 px-2 text-xs">{c.customer_comune}</td>
                        <td className="text-right tabular-nums font-semibold">{fmtEur(c.total_netto)}</td>
                        <td className="text-right tabular-nums">{c.n_invoices}</td>
                        <td className="text-right tabular-nums text-muted-foreground">{fmtEur(c.avg_invoice)}</td>
                        <td className="py-1.5 px-2">
                          <div className="flex flex-wrap gap-1">
                            {(c.services || []).map(s => (
                              <Badge key={s.service_type} variant="outline" className="text-[9px]" style={{ borderColor: SERVICE_COLORS[s.service_type], color: SERVICE_COLORS[s.service_type] }}>
                                {labels[s.service_type] || s.service_type}: {fmtEur(s.total)}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: TREND */}
          <TabsContent value="trend" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Trend Mensile per Servizio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={monthlyChartData} margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {monthlyKeys.map((k, i) => {
                      const colorKey = Object.keys(labels).find(sk => labels[sk] === k) || "other";
                      return <Bar key={k} dataKey={k} stackId="a" fill={SERVICE_COLORS[colorKey] || "#94a3b8"} />;
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: MARGINE & PRICING */}
          <TabsContent value="margin" className="space-y-4">
            <MarginPricingTab clientId={clientId} year={year} />
          </TabsContent>

          {/* TAB: MARGINE EFFETTIVO (da Excel ore/materiale + fatture acquisto) */}
          <TabsContent value="margin_eff" className="space-y-4">
            <MarginEffettivoTab clientId={clientId} year={year} />
          </TabsContent>

          {/* TAB: COSTO ORARIO (basato su durate medie servizio + costo collaboratori netto) */}
          <TabsContent value="hourly" className="space-y-4">
            <HourlyCostTab clientId={clientId} year={year} serviceLabels={labels} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ServiceInvoicesPage;

// ====================== MARGIN & PRICING TAB ======================

const STATUS_COLOR = {
  ok: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500", emoji: "🟢" },
  warn: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", dot: "bg-amber-500", emoji: "🟡" },
  bad: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", dot: "bg-red-500", emoji: "🔴" },
  no_data: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-700", dot: "bg-slate-400", emoji: "⚪️" },
};

const MarginPricingTab = ({ clientId, year }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [targetMargin, setTargetMargin] = useState(30); // %
  const [marginData, setMarginData] = useState(null);
  // costs[serviceType][month][cost_category] = amount
  const [costs, setCosts] = useState({});
  const [selectedService, setSelectedService] = useState(null);

  const loadData = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const [marginRes, costsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/service-invoices/margin?year=${year}&target_margin=${targetMargin / 100}`),
        axios.get(`${API}/clients/${clientId}/service-direct-costs?year=${year}`),
      ]);
      setMarginData(marginRes.data);
      // Build costs nested map
      const nested = {};
      (costsRes.data.rows || []).forEach(r => {
        if (!nested[r.service_type]) nested[r.service_type] = {};
        if (!nested[r.service_type][r.month]) nested[r.service_type][r.month] = {};
        nested[r.service_type][r.month][r.cost_category] = r.amount;
      });
      setCosts(nested);
      // Pick default service: first one with revenue
      const firstSrv = marginRes.data.services?.[0]?.service_type;
      setSelectedService(prev => prev || firstSrv);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento margine");
    } finally {
      setLoading(false);
    }
  }, [clientId, year, targetMargin]);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [clientId, year]);

  // Re-call margin endpoint when target changes (lightweight)
  const refetchMargin = useCallback(async (newTarget) => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/service-invoices/margin?year=${year}&target_margin=${newTarget / 100}`);
      setMarginData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [clientId, year]);

  const handleCostChange = (service, month, category, value) => {
    const v = value === "" ? "" : parseFloat(value.replace(",", "."));
    setCosts(prev => {
      const next = { ...prev };
      if (!next[service]) next[service] = {};
      if (!next[service][month]) next[service][month] = {};
      next[service][month][category] = isNaN(v) ? "" : v;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Flatten nested costs to rows
      const rows = [];
      Object.entries(costs).forEach(([service, monthsObj]) => {
        Object.entries(monthsObj).forEach(([month, catsObj]) => {
          Object.entries(catsObj).forEach(([cat, amt]) => {
            const num = typeof amt === "number" ? amt : parseFloat(amt);
            if (!isNaN(num) && num !== 0) {
              rows.push({ month: parseInt(month), service_type: service, cost_category: cat, amount: num });
            }
          });
        });
      });
      await axios.put(`${API}/clients/${clientId}/service-direct-costs`, { year, rows });
      toast.success("Costi salvati");
      await loadData();
    } catch (e) {
      console.error(e);
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!year) {
      toast.error("Seleziona un anno prima di scaricare il template");
      return;
    }
    try {
      const res = await axios.get(`${API}/clients/${clientId}/service-direct-costs/template?year=${year}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Costi_Diretti_${year}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Template scaricato — invialo al cliente per la compilazione");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Errore download template");
    }
  };

  const fileTemplateInputRef = useRef(null);
  const handleImportTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/clients/${clientId}/service-direct-costs/import?year=${year}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { inserted, sheets_processed } = res.data;
      toast.success(`Template importato: ${inserted} valori su ${sheets_processed?.length || 0} servizi`);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Errore import template");
    } finally {
      if (fileTemplateInputRef.current) fileTemplateInputRef.current.value = "";
    }
  };

  // Banner template SEMPRE visibile (anche durante loading o se manca anno)
  const TemplateBanner = (
    <Card className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="text-3xl">📋</div>
            <div>
              <div className="font-semibold text-blue-900">Raccolta Costi Diretti dal Cliente</div>
              <div className="text-xs text-blue-700 mt-0.5">
                Scarica il template Excel da consegnare al cliente — quando te lo restituisce compilato, ricaricalo qui.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={handleDownloadTemplate}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="download-template-btn-top"
              disabled={!year}
            >
              <Download className="h-4 w-4" />
              Scarica Template Excel
            </Button>
            <input
              ref={fileTemplateInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleImportTemplate}
              style={{ display: "none" }}
              data-testid="import-template-input"
            />
            <Button
              variant="outline"
              onClick={() => fileTemplateInputRef.current?.click()}
              className="gap-2 border-blue-400 text-blue-700 hover:bg-blue-100"
              data-testid="import-template-btn-top"
              disabled={!year}
            >
              <FileUp className="h-4 w-4" />
              Carica Compilato
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) return (
    <div className="space-y-4">
      {TemplateBanner}
      <div className="text-center py-8 text-muted-foreground">Caricamento margini…</div>
    </div>
  );
  if (!marginData) return TemplateBanner;

  const categories = marginData.categories || [];
  const categoryLabels = marginData.category_labels || {};
  const services = marginData.services || [];
  const overall = marginData.overall || {};

  const selectedSrvData = services.find(s => s.service_type === selectedService);

  // Compute month totals for selected service
  const monthCellValue = (serviceType, month, category) => {
    return costs[serviceType]?.[month]?.[category] ?? "";
  };
  const monthRowTotal = (serviceType, month) => {
    if (!costs[serviceType]?.[month]) return 0;
    return categories.reduce((sum, c) => {
      const v = costs[serviceType][month][c];
      const num = typeof v === "number" ? v : parseFloat(v);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  };
  const categoryYearTotal = (serviceType, category) => {
    let sum = 0;
    for (let m = 1; m <= 12; m++) {
      const v = costs[serviceType]?.[m]?.[category];
      const num = typeof v === "number" ? v : parseFloat(v);
      if (!isNaN(num)) sum += num;
    }
    return sum;
  };
  const serviceYearTotal = (serviceType) => {
    return categories.reduce((s, c) => s + categoryYearTotal(serviceType, c), 0);
  };

  return (
    <div className="space-y-4">
      {/* CTA TEMPLATE — Banner sempre visibile in alto */}
      {TemplateBanner}

      {/* HEADER: Target margin slider + overall card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4 text-violet-600" />
            Margine Servizio — Anno {year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Slider */}
            <div className="lg:col-span-1 border rounded-lg p-4 bg-violet-50 border-violet-200">
              <div className="text-xs uppercase tracking-wide font-semibold text-violet-700 mb-1">Margine Target</div>
              <div className="text-3xl font-bold text-violet-900 mb-3 tabular-nums">{targetMargin}%</div>
              <Slider
                value={[targetMargin]}
                onValueChange={(v) => setTargetMargin(v[0])}
                onValueCommit={(v) => refetchMargin(v[0])}
                min={10}
                max={60}
                step={1}
                data-testid="target-margin-slider"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>10%</span><span>30%</span><span>60%</span>
              </div>
              <div className="text-xs text-violet-600 mt-2">
                Trascina per ricalcolare i prezzi suggeriti.
              </div>
            </div>
            {/* Overall metrics */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Ricavi YTD</div>
                <div className="text-xl font-bold tabular-nums text-emerald-900">{fmtEur(overall.revenue)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-orange-50 border-orange-200">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-orange-700">Costi diretti</div>
                <div className="text-xl font-bold tabular-nums text-orange-900">{fmtEur(overall.cost_total)}</div>
              </div>
              <div className={`border rounded-lg p-3 ${overall.margin >= 0 ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}>
                <div className={`text-[10px] uppercase tracking-wide font-semibold ${overall.margin >= 0 ? "text-blue-700" : "text-red-700"}`}>Margine €</div>
                <div className={`text-xl font-bold tabular-nums ${overall.margin >= 0 ? "text-blue-900" : "text-red-900"}`}>{fmtEur(overall.margin)}</div>
              </div>
              <div className={`border rounded-lg p-3 ${overall.margin_pct == null ? "bg-slate-50 border-slate-200" : (overall.margin_pct >= targetMargin / 100 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200")}`}>
                <div className="text-[10px] uppercase tracking-wide font-semibold">Margine %</div>
                <div className="text-xl font-bold tabular-nums">{overall.margin_pct == null ? "—" : `${(overall.margin_pct * 100).toFixed(1)}%`}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PER-SERVICE RESULT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="service-margin-cards">
        {services.map(s => {
          const col = STATUS_COLOR[s.status] || STATUS_COLOR.no_data;
          const isSelected = selectedService === s.service_type;
          return (
            <div
              key={s.service_type}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${col.bg} ${isSelected ? "border-violet-500 ring-2 ring-violet-200" : col.border}`}
              onClick={() => setSelectedService(s.service_type)}
              data-testid={`service-card-${s.service_type}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge style={{ backgroundColor: SERVICE_COLORS[s.service_type] || "#94a3b8", color: "white" }}>
                    {s.service_label}
                  </Badge>
                  <span className="text-base">{col.emoji}</span>
                </div>
                <span className={`text-xs font-semibold ${col.text}`}>
                  {s.margin_pct == null ? "n/d" : `${(s.margin_pct * 100).toFixed(1)}%`}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">Ricavi</div>
                  <div className="font-semibold tabular-nums">{fmtEur(s.revenue)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Costi</div>
                  <div className="font-semibold tabular-nums">{fmtEur(s.cost_total)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Margine €</div>
                  <div className={`font-semibold tabular-nums ${s.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtEur(s.margin)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Quantità</div>
                  <div className="font-semibold tabular-nums">{fmtNum(s.quantity)}</div>
                </div>
              </div>
              {/* Pricing suggestion */}
              {s.median_unit_price > 0 && (
                <div className="mt-2 pt-2 border-t border-dashed">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Prezzo €/unità (mediana)</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold tabular-nums">{fmtEur(s.median_unit_price)}</span>
                    {s.suggested_unit_price != null && (
                      <>
                        <span className="text-muted-foreground text-xs">→</span>
                        <span className="text-sm font-bold tabular-nums text-violet-700">
                          {fmtEur(s.suggested_unit_price)}
                        </span>
                        <span className="text-[10px] font-semibold text-violet-600">
                          (+{s.price_increase_pct.toFixed(1)}%)
                        </span>
                      </>
                    )}
                  </div>
                  {s.suggested_unit_price == null && s.cost_total > 0 && s.margin_pct != null && (
                    <div className="text-[11px] text-emerald-600 italic">✓ Già sopra il target — nessun aumento necessario</div>
                  )}
                  {s.suggested_unit_price == null && s.cost_total === 0 && (
                    <div className="text-[11px] text-muted-foreground italic">Inserisci costi per vedere prezzo target</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* COSTS EDITOR */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="h-4 w-4 text-orange-600" />
              Costi diretti mensili — {selectedSrvData?.service_label || "—"}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={selectedService || ""} onValueChange={setSelectedService}>
                <SelectTrigger className="w-44" data-testid="service-selector">
                  <SelectValue placeholder="Servizio" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.service_type} value={s.service_type}>{s.service_label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-costs-btn">
                <Save className="h-4 w-4" />
                {saving ? "Salvataggio…" : "Salva"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {selectedService ? (
            <table className="w-full text-sm" data-testid="costs-editor-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-2 px-2 font-semibold sticky left-0 bg-slate-50">Mese</th>
                  {categories.map(c => (
                    <th key={c} className="text-right py-2 px-2 font-semibold">{categoryLabels[c]}</th>
                  ))}
                  <th className="text-right py-2 px-2 font-semibold bg-slate-100">Totale</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS_IT.map((monthLabel, idx) => {
                  const month = idx + 1;
                  const rowTotal = monthRowTotal(selectedService, month);
                  return (
                    <tr key={month} className="border-b hover:bg-slate-50">
                      <td className="py-1 px-2 font-medium sticky left-0 bg-white">{monthLabel}</td>
                      {categories.map(c => (
                        <td key={c} className="py-1 px-2">
                          <Input
                            type="number"
                            step="any"
                            value={monthCellValue(selectedService, month, c)}
                            onChange={(e) => handleCostChange(selectedService, month, c, e.target.value)}
                            className="text-right tabular-nums h-8 w-28 ml-auto"
                            placeholder="0"
                            data-testid={`cost-input-${selectedService}-${month}-${c}`}
                          />
                        </td>
                      ))}
                      <td className="text-right tabular-nums font-semibold bg-slate-50">{rowTotal > 0 ? fmtEur(rowTotal) : "—"}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 bg-slate-100 font-bold">
                  <td className="py-2 px-2 sticky left-0 bg-slate-100">TOTALE ANNO</td>
                  {categories.map(c => (
                    <td key={c} className="text-right tabular-nums py-2 px-2">{fmtEur(categoryYearTotal(selectedService, c))}</td>
                  ))}
                  <td className="text-right tabular-nums py-2 px-2 bg-slate-200">{fmtEur(serviceYearTotal(selectedService))}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="text-center py-6 text-muted-foreground">Seleziona un servizio per inserire i costi</div>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            Inserisci i costi diretti reali sostenuti per ciascun mese e categoria. Lascia 0 per le voci non applicabili.
            Premi <strong>Salva</strong> per aggiornare margine e pricing suggerito.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};


// ====================== CUSTOMER WHALE CURVE SECTION ======================

const CustomerWhaleSection = ({ topCustomers, labels, clientId, year, onSynced }) => {
  const [syncing, setSyncing] = useState(false);
  const [allocating, setAllocating] = useState(false);

  // Compute Pareto/Whale curve data from all customers
  const sorted = [...(topCustomers || [])].sort((a, b) => (b.total_netto || 0) - (a.total_netto || 0));
  const totalRevenue = sorted.reduce((s, c) => s + (c.total_netto || 0), 0);
  let cumulative = 0;
  const chartData = sorted.map((c, i) => {
    cumulative += c.total_netto || 0;
    const cumPct = totalRevenue > 0 ? (cumulative / totalRevenue) * 100 : 0;
    return {
      rank: i + 1,
      name: c.customer_denominazione || `#${i + 1}`,
      revenue: c.total_netto || 0,
      cumPct: parseFloat(cumPct.toFixed(2)),
    };
  });

  // Find how many customers cover 80% / 50% / 20%
  const customers80 = chartData.findIndex(d => d.cumPct >= 80);
  const n80 = customers80 >= 0 ? customers80 + 1 : sorted.length;
  const customers50 = chartData.findIndex(d => d.cumPct >= 50);
  const n50 = customers50 >= 0 ? customers50 + 1 : sorted.length;
  const pct80 = sorted.length > 0 ? (n80 / sorted.length) * 100 : 0;

  // Concentration risk: % of revenue from top 2 customers (key-account risk)
  const top2Revenue = sorted.slice(0, 2).reduce((s, c) => s + (c.total_netto || 0), 0);
  const top2Pct = totalRevenue > 0 ? (top2Revenue / totalRevenue) * 100 : 0;
  const top10Revenue = sorted.slice(0, 10).reduce((s, c) => s + (c.total_netto || 0), 0);
  const top10Pct = totalRevenue > 0 ? (top10Revenue / totalRevenue) * 100 : 0;

  const handleManualSync = async () => {
    if (!year) {
      toast.error("Seleziona un anno prima di sincronizzare");
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/service-invoices/sync-to-whale-curve?year=${year}`);
      const { created, updated } = res.data;
      toast.success(`🐋 Whale Curve aggiornata: ${created} nuovi, ${updated} aggiornati`);
      onSynced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const handleAllocateVariableCosts = async () => {
    if (!year) {
      toast.error("Seleziona un anno prima di allocare");
      return;
    }
    if (!window.confirm("Distribuire i costi variabili del bilancio su tutti i 216 clienti in proporzione al fatturato? Questa azione sovrascriverà eventuali costi variabili già inseriti manualmente.")) return;
    setAllocating(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/customers/allocate-variable-costs?year=${year}`);
      const { customers_updated, total_variable_costs_allocated, message } = res.data;
      toast.success(message || `Costi variabili distribuiti su ${customers_updated} clienti (${fmtEur(total_variable_costs_allocated)})`);
      onSynced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore allocazione costi variabili");
    } finally {
      setAllocating(false);
    }
  };

  if (sorted.length === 0) return null;

  return (
    <Card data-testid="whale-curve-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="text-xl">🐋</span>
            Curva Whale (Pareto) — {sorted.length} clienti {year || ""}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualSync}
              disabled={syncing}
              className="gap-2"
              data-testid="sync-whale-btn"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizzazione…" : "Aggiorna Whale Curve"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAllocateVariableCosts}
              disabled={allocating}
              className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="allocate-vc-btn"
              title="Distribuisce i costi variabili del bilancio su tutti i clienti proporzionalmente al fatturato (senza distinzioni)"
            >
              <Wallet className={`h-4 w-4 ${allocating ? "animate-pulse" : ""}`} />
              {allocating ? "Distribuzione…" : "Distribuisci Costi Variabili"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="border rounded-lg p-3 bg-slate-50 border-slate-200">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-700">Clienti totali</div>
            <div className="text-xl font-bold tabular-nums text-slate-900">{sorted.length}</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{fmtEur(totalRevenue)} totali</div>
          </div>
          <div className={`border rounded-lg p-3 ${top2Pct >= 25 ? "bg-red-50 border-red-300" : top2Pct >= 15 ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"}`}>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-700">⚠️ Top 2 clienti</div>
            <div className="text-xl font-bold tabular-nums">{top2Pct.toFixed(1)}% <span className="text-xs font-medium text-muted-foreground">del fatturato</span></div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Rischio key-account</div>
          </div>
          <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Top 10 clienti</div>
            <div className="text-xl font-bold tabular-nums text-blue-900">{top10Pct.toFixed(1)}% <span className="text-xs font-medium">del fatturato</span></div>
            <div className="text-[10px] text-blue-700 mt-0.5">Clienti chiave</div>
          </div>
          <div className="border rounded-lg p-3 bg-violet-50 border-violet-200">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-violet-700">80% Fatturato</div>
            <div className="text-xl font-bold tabular-nums text-violet-900">{n80} <span className="text-xs font-medium">clienti</span></div>
            <div className="text-[10px] text-violet-700 mt-0.5">su {sorted.length} totali ({pct80.toFixed(0)}%)</div>
          </div>
        </div>

        {/* Concentration risk banner (più chiaro del precedente Pareto) */}
        {top2Pct >= 25 ? (
          <div className="rounded-lg p-3 mb-4 text-sm border bg-red-50 border-red-200 text-red-900">
            <strong>🚨 Alta concentrazione:</strong> i tuoi <strong>2 clienti più grandi</strong> ({sorted[0]?.name} e {sorted[1]?.name}) generano da soli il <strong>{top2Pct.toFixed(1)}%</strong> del fatturato.
            Se perdi uno di loro, perdi {(top2Pct / 2).toFixed(0)}% dei ricavi annui. <strong>Priorità: diversificare</strong> e proteggere queste relazioni.
          </div>
        ) : top2Pct >= 15 ? (
          <div className="rounded-lg p-3 mb-4 text-sm border bg-amber-50 border-amber-200 text-amber-900">
            <strong>⚠️ Concentrazione media:</strong> i 2 clienti più grandi ({sorted[0]?.name} e {sorted[1]?.name}) fanno il <strong>{top2Pct.toFixed(1)}%</strong> del fatturato.
            Tieni d'occhio: una loro perdita avrebbe impatto importante.
          </div>
        ) : (
          <div className="rounded-lg p-3 mb-4 text-sm border bg-emerald-50 border-emerald-200 text-emerald-900">
            <strong>✅ Distribuzione sana:</strong> il fatturato è ben distribuito — nessun singolo cliente concentra più del {(sorted[0] ? (sorted[0].total_netto/totalRevenue*100) : 0).toFixed(1)}% dei ricavi.
            Bassa dipendenza da key-account.
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-4 italic">
          📐 <strong>Come leggere la regola 80/20</strong>: l'80% del fatturato è generato dai primi {n80} clienti (i tuoi "VIP").
          I restanti {sorted.length - n80} clienti coprono il 20% — sono micro-clienti spesso ad alto costo di gestione.
          Sopra il grafico: barre = fatturato per cliente | linea rossa = % cumulato.
        </p>

        {/* Pareto chart */}
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="rank"
              tick={{ fontSize: 10 }}
              label={{ value: "Clienti (ordinati per fatturato)", position: "insideBottom", offset: -5, fontSize: 11 }}
            />
            <YAxis yAxisId="left" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Fatturato") return fmtEur(value);
                if (name === "Cumulato %") return `${value}%`;
                return value;
              }}
              labelFormatter={(rank) => {
                const c = chartData[rank - 1];
                return c ? `#${rank}: ${c.name}` : `#${rank}`;
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="revenue" name="Fatturato" fill="#3b82f6" />
            <Line yAxisId="right" type="monotone" dataKey="cumPct" name="Cumulato %" stroke="#ef4444" strokeWidth={2} dot={false} />
            <ReferenceLine yAxisId="right" y={80} stroke="#94a3b8" strokeDasharray="5 5" label={{ value: "80%", position: "right", fontSize: 10 }} />
          </ComposedChart>
        </ResponsiveContainer>

        <div className="text-xs text-muted-foreground mt-3 flex items-start gap-2">
          <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            La <strong>Whale Curve</strong> dei clienti è già stata <strong>sincronizzata automaticamente</strong> da queste fatture XML.
            Vai sulla scheda <strong>🐳 Whale Curve</strong> del cliente per aggiungere ore lavorate, costi diretti e calcolare la marginalità per cliente.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};


// ====================== HOURLY COST TAB ======================
// Permette all'utente di:
//  1. Inserire la durata media (minuti) per ogni TIPO di servizio
//  2. Inserire il costo variabile totale collaboratori del periodo + eventuali quote NON cliniche
//  3. Calcolare automaticamente: ore totali → costo orario → margine per servizio

const HourlyCostTab = ({ clientId, year, serviceLabels }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [durations, setDurations] = useState({});
  const [opCfg, setOpCfg] = useState({
    total_variable_cost: 0,
    period_end_month: 12,
    manager_name: "",
    manager_invoice_amount: 0,
    manager_non_treatment_pct: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, anRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/service-hourly-config?year=${year}`),
        axios.get(`${API}/clients/${clientId}/service-hourly-analysis?year=${year}`),
      ]);
      setDurations(cfgRes.data.durations || {});
      setOpCfg({
        total_variable_cost: cfgRes.data.operator_config?.total_variable_cost || 0,
        period_end_month: cfgRes.data.operator_config?.period_end_month || 12,
        manager_name: cfgRes.data.operator_config?.manager_name || "",
        manager_invoice_amount: cfgRes.data.operator_config?.manager_invoice_amount || 0,
        manager_non_treatment_pct: cfgRes.data.operator_config?.manager_non_treatment_pct || 0,
      });
      setAnalysis(anRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento configurazione");
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/service-hourly-config`, {
        durations,
        operator_config: { ...opCfg, year },
      });
      toast.success("Configurazione salvata");
      await fetchData();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Caricamento…</div>;

  const activeTypes = (analysis?.by_service || []).map(s => s.service_type);
  const allTypes = Array.from(new Set([...activeTypes, ...Object.keys(serviceLabels || {})]))
    .filter(t => t !== 'other' || activeTypes.includes('other'));

  const fmtH = (h) => h?.toLocaleString("it-IT", { maximumFractionDigits: 1 });

  return (
    <div className="space-y-4">
      {/* CONFIGURAZIONE */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            ⚙️ Configurazione costo collaboratori &amp; durate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-semibold text-sm mb-3 text-slate-700">Costo variabile collaboratori (anno {year})</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-600">Costo totale collaboratori (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={opCfg.total_variable_cost}
                  onChange={(e) => setOpCfg({ ...opCfg, total_variable_cost: parseFloat(e.target.value) || 0 })}
                  data-testid="hourly-total-cost-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Fino al mese</label>
                <Select
                  value={String(opCfg.period_end_month)}
                  onValueChange={(v) => setOpCfg({ ...opCfg, period_end_month: parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="hourly-period-end-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS_IT.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m} ({i + 1})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-3 text-slate-700">Quota non clinica (es. socio che fa anche ricerca clienti/gestione)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600">Nome collaboratore (opzionale)</label>
                <Input
                  value={opCfg.manager_name}
                  onChange={(e) => setOpCfg({ ...opCfg, manager_name: e.target.value })}
                  placeholder="es. Lorenzo"
                  data-testid="hourly-manager-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Fatturato totale (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={opCfg.manager_invoice_amount}
                  onChange={(e) => setOpCfg({ ...opCfg, manager_invoice_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="hourly-manager-amount-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">% non clinica (0-100)</label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={Math.round((opCfg.manager_non_treatment_pct || 0) * 100)}
                  onChange={(e) => setOpCfg({ ...opCfg, manager_non_treatment_pct: (parseFloat(e.target.value) || 0) / 100 })}
                  data-testid="hourly-manager-pct-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              💡 Es. Lorenzo fattura 8.480€ e il 90% del suo lavoro è ricerca clienti/gestione → il sistema scarica{" "}
              <strong>{fmtEur(opCfg.manager_invoice_amount * opCfg.manager_non_treatment_pct)}</strong> dal costo variabile.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-3 text-slate-700">Durata media per servizio (minuti)</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allTypes.map(st => (
                <div key={st} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: SERVICE_COLORS[st] || "#94a3b8" }}
                  />
                  <label className="text-xs text-slate-700 flex-1 truncate" title={serviceLabels[st] || st}>
                    {serviceLabels[st] || st}
                  </label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    className="w-20"
                    value={durations[st] || 0}
                    onChange={(e) => setDurations({ ...durations, [st]: parseFloat(e.target.value) || 0 })}
                    data-testid={`hourly-duration-${st}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="hourly-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvataggio…" : "Salva e ricalcola"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {analysis && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">📊 Risultato — fino a {MONTHS_IT[(analysis.period_end_month || 1) - 1]} {analysis.year}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPIBox label="Costo collaboratori (lordo)" value={fmtEur(analysis.total_variable_cost)} hint={analysis.manager_name ? `${analysis.manager_name}: ${fmtEur(analysis.manager_invoice_amount)} (${Math.round(analysis.manager_non_treatment_pct * 100)}% non clinica)` : null} />
              <KPIBox label="Scaricato (non clinico)" value={`- ${fmtEur(analysis.manager_excluded_amount)}`} color="text-rose-700" />
              <KPIBox label="Costo netto trattamenti" value={fmtEur(analysis.cost_net_for_treatments)} color="text-blue-700" emphasized />
              <KPIBox label="Ore totali lavorate" value={`${fmtH(analysis.total_hours)} h`} hint={`${analysis.by_service?.length || 0} tipi servizio`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KPIBox label="💰 Costo orario" value={fmtEur(analysis.hourly_rate) + "/h"} emphasized color="text-emerald-700" />
              <KPIBox label="Ricavi totali" value={fmtEur(analysis.total_revenue)} />
              <KPIBox label="Margine totale" value={fmtEur(analysis.total_margin)} color={analysis.total_margin >= 0 ? "text-emerald-700" : "text-rose-700"} emphasized />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hourly-margin-table">
                <thead>
                  <tr className="border-b text-xs uppercase text-slate-500">
                    <th className="text-left py-2 px-2">Servizio</th>
                    <th className="text-right py-2 px-2">Quantità</th>
                    <th className="text-right py-2 px-2">Durata (min)</th>
                    <th className="text-right py-2 px-2">Ore totali</th>
                    <th className="text-right py-2 px-2">Ricavo</th>
                    <th className="text-right py-2 px-2">Costo</th>
                    <th className="text-right py-2 px-2">Margine</th>
                    <th className="text-right py-2 px-2">Margine %</th>
                  </tr>
                </thead>
                <tbody>
                  {(analysis.by_service || []).map(s => (
                    <tr key={s.service_type} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SERVICE_COLORS[s.service_type] || "#94a3b8" }} />
                          <span className="font-medium">{s.label}</span>
                        </div>
                      </td>
                      <td className="text-right py-2 px-2">{fmtNum(s.total_quantity)}</td>
                      <td className="text-right py-2 px-2 text-slate-600">{s.duration_minutes || "—"}</td>
                      <td className="text-right py-2 px-2">{fmtH(s.hours)}</td>
                      <td className="text-right py-2 px-2 font-medium">{fmtEur(s.revenue)}</td>
                      <td className="text-right py-2 px-2 text-rose-600">{fmtEur(s.cost)}</td>
                      <td className={`text-right py-2 px-2 font-semibold ${s.margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtEur(s.margin)}</td>
                      <td className={`text-right py-2 px-2 ${s.margin_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{s.margin_pct?.toFixed(1)}%</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-slate-100">
                    <td className="py-2 px-2">TOTALE</td>
                    <td></td>
                    <td></td>
                    <td className="text-right py-2 px-2">{fmtH(analysis.total_hours)}</td>
                    <td className="text-right py-2 px-2">{fmtEur(analysis.total_revenue)}</td>
                    <td className="text-right py-2 px-2 text-rose-700">{fmtEur(analysis.total_cost_allocated)}</td>
                    <td className={`text-right py-2 px-2 ${analysis.total_margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtEur(analysis.total_margin)}</td>
                    <td className={`text-right py-2 px-2 ${analysis.total_margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {analysis.total_revenue > 0 ? ((analysis.total_margin / analysis.total_revenue) * 100).toFixed(1) : "0.0"}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {analysis.total_hours === 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                ⚠️ Le ore totali sono 0 — compila le <strong>durate medie</strong> per ogni servizio sopra e clicca <strong>Salva e ricalcola</strong>.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const KPIBox = ({ label, value, hint, color, emphasized }) => (
  <div className={`rounded-lg border p-3 ${emphasized ? "bg-slate-50 border-slate-300" : "bg-white"}`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-lg font-bold ${color || "text-slate-800"}`}>{value}</div>
    {hint && <div className="text-[10px] text-slate-500 mt-1 truncate" title={hint}>{hint}</div>}
  </div>
);


// ====================== MARGINE EFFETTIVO TAB ======================
// Calcolo margine da: Ricavi (fatture emesse) - Materiale VC (Excel + fatture acquisto) - Costo Ore (personale bilancio / ore totali)
const MarginEffettivoTab = ({ clientId, year }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [uploadingPurchase, setUploadingPurchase] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const excelInputRef = useRef(null);
  const purchaseInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/service-margin?year=${year}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento margine");
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { load(); }, [load]);

  const handleExcelUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingExcel(true);
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append("files", f));
    try {
      const r = await axios.post(`${API}/clients/${clientId}/service-invoices/import-excel-costs?year=${year}`, fd);
      toast.success(`Import Excel completato: ${r.data.matched_rows}/${r.data.total_excel_rows} righe matchate`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore import Excel");
    } finally {
      setUploadingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
    }
  };

  const handlePurchaseUpload = async (file) => {
    if (!file) return;
    setUploadingPurchase(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/clients/${clientId}/service-invoices/import-purchases`, fd);
      toast.success(`Fatture acquisto: ${r.data.inserted} nuove, ${r.data.skipped_duplicates} duplicate`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore import acquisti");
    } finally {
      setUploadingPurchase(false);
      if (purchaseInputRef.current) purchaseInputRef.current.value = "";
    }
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Caricamento…</div>;
  if (!data) return <div className="p-6 text-center text-slate-500">Nessun dato disponibile</div>;

  const totalRevenue = data.by_service.reduce((s, x) => s + x.revenue, 0);
  const totalMargin = data.by_service.reduce((s, x) => s + x.margin, 0);
  const totalVC = data.by_service.reduce((s, x) => s + x.total_variable_cost, 0);
  const avgMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue * 100) : 0;

  return (
    <div className="space-y-4">
      {/* HEADER + UPLOAD */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">Margine Effettivo per Servizio — {year}</CardTitle>
              <div className="text-xs text-slate-500">Ricavi (fatture emesse) − Materiale VC (Excel + fornitori) − Costo Ore</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <input ref={excelInputRef} type="file" accept=".xls,.xlsx" multiple hidden onChange={(e) => handleExcelUpload(e.target.files)} data-testid="upload-excel-input" />
              <Button size="sm" variant="outline" disabled={uploadingExcel} onClick={() => excelInputRef.current?.click()} data-testid="btn-upload-excel">
                <FileUp className="h-3.5 w-3.5 mr-1" />{uploadingExcel ? "Caricamento…" : "Import Ore/Materiale (Excel)"}
              </Button>
              <input ref={purchaseInputRef} type="file" accept=".zip,.xml" hidden onChange={(e) => handlePurchaseUpload(e.target.files?.[0])} data-testid="upload-purchases-input" />
              <Button size="sm" variant="outline" disabled={uploadingPurchase} onClick={() => purchaseInputRef.current?.click()} data-testid="btn-upload-purchases">
                <Upload className="h-3.5 w-3.5 mr-1" />{uploadingPurchase ? "Caricamento…" : "Import Fatture Acquisto (ZIP)"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowMappingModal(true)} data-testid="btn-supplier-mapping">
                🔗 Mapping Fornitori
              </Button>
              <Button size="sm" variant="outline" onClick={load} data-testid="btn-reload-margin"><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* KPI RIEPILOGO */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <KPIBox label="Ricavi tot" value={fmtEur(totalRevenue)} emphasized />
            <KPIBox label="Costi Variabili" value={fmtEur(totalVC)} color="text-red-600" />
            <KPIBox label="Margine tot" value={fmtEur(totalMargin)} color="text-emerald-600" emphasized />
            <KPIBox label="Margine %" value={`${avgMarginPct.toFixed(1)}%`} color={avgMarginPct >= 30 ? "text-emerald-600" : avgMarginPct >= 15 ? "text-amber-600" : "text-red-600"} />
            <KPIBox label="Costo €/ora" value={`${data.hourly_rate_computed?.toFixed(2) || 0} €/h`} hint={`${fmtNum(data.total_hours)}h · Personale bilancio ${fmtEur(data.total_personnel_cost_bilancio)}`} />
          </div>

          {/* TABELLA MARGINE PER SERVIZIO */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Servizio</th>
                  <th className="p-2 text-right">Ricavi</th>
                  <th className="p-2 text-right">Righe fatt.</th>
                  <th className="p-2 text-right">Ore</th>
                  <th className="p-2 text-right">Interv.</th>
                  <th className="p-2 text-right" title="Materiale VC dal file Excel Claudia">Mat. Excel</th>
                  <th className="p-2 text-right" title="Materiale VC dalle fatture acquisto fornitori">Mat. Forn.</th>
                  <th className="p-2 text-right" title="Costo delle ore = ore × costo orario calcolato">Costo Ore</th>
                  <th className="p-2 text-right">Costi VC Tot</th>
                  <th className="p-2 text-right font-bold">Margine €</th>
                  <th className="p-2 text-right font-bold">Marg. %</th>
                  <th className="p-2 text-right" title="Margine per ora di lavoro">€/ora</th>
                </tr>
              </thead>
              <tbody>
                {data.by_service.map(s => {
                  const marginColor = s.margin_pct >= 40 ? "text-emerald-700 bg-emerald-50" : s.margin_pct >= 20 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
                  return (
                    <tr key={s.service_type} className="border-b hover:bg-slate-50" data-testid={`margin-row-${s.service_type}`}>
                      <td className="p-2 font-semibold">
                        <span className="inline-block h-3 w-3 rounded-full mr-2" style={{background: SERVICE_COLORS[s.service_type] || "#94a3b8"}} />
                        {s.label}
                      </td>
                      <td className="p-2 text-right tabular-nums">{fmtEur(s.revenue)}</td>
                      <td className="p-2 text-right text-slate-500 tabular-nums">{s.n_lines}</td>
                      <td className="p-2 text-right tabular-nums">{fmtNum(s.hours)}</td>
                      <td className="p-2 text-right text-slate-500 tabular-nums">{s.n_interventions > 0 ? fmtNum(s.n_interventions) : "—"}</td>
                      <td className="p-2 text-right tabular-nums text-slate-600">{fmtEur(s.material_excel)}</td>
                      <td className="p-2 text-right tabular-nums text-slate-600">{fmtEur(s.material_suppliers)}</td>
                      <td className="p-2 text-right tabular-nums text-slate-600">{fmtEur(s.labor_cost)}</td>
                      <td className="p-2 text-right tabular-nums text-red-600">{fmtEur(s.total_variable_cost)}</td>
                      <td className={`p-2 text-right tabular-nums font-bold ${marginColor}`}>{fmtEur(s.margin)}</td>
                      <td className={`p-2 text-right tabular-nums font-bold ${marginColor}`}>{s.margin_pct.toFixed(1)}%</td>
                      <td className="p-2 text-right tabular-nums">{s.margin_per_hour ? `${s.margin_per_hour.toFixed(1)}€` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 text-xs text-slate-500 space-y-1">
            <div>💡 <strong>Costo orario</strong> = costo Personale del bilancio ({fmtEur(data.total_personnel_cost_bilancio)}) ÷ ore totali lavorate ({fmtNum(data.total_hours)}) = <strong>{data.hourly_rate_computed?.toFixed(2)} €/h</strong>.</div>
            {data.excluded_personnel_bilancio > 0 && (
              <div>ℹ️ <strong>Esclusi {fmtEur(data.excluded_personnel_bilancio)}</strong> di fornitori "personale somministrato" (Just on Business, Synergie) — già inclusi nel Personale del bilancio.</div>
            )}
            {data.unallocated_purchases > 0 && (
              <div>⚠️ <strong>{fmtEur(data.unallocated_purchases)}</strong> di acquisti fornitori NON allocati a nessun servizio (probabilmente spese generali/costi fissi). Vedi lista sotto.</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* TOP UNMAPPED SUPPLIERS */}
      {data.unmapped_suppliers_top && data.unmapped_suppliers_top.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Fornitori NON mappati (Top {data.unmapped_suppliers_top.length})</CardTitle>
            <div className="text-xs text-slate-500">Assegna un servizio dalla schermata "Mapping Fornitori" per allocarli</div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr><th className="p-2 text-left">Fornitore</th><th className="p-2 text-right">Imponibile</th></tr>
              </thead>
              <tbody>
                {data.unmapped_suppliers_top.map((u, i) => (
                  <tr key={i} className="border-b" data-testid={`unmapped-supplier-${i}`}>
                    <td className="p-2">{u.name}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{fmtEur(u.imponibile)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* MODALE MAPPING FORNITORI */}
      {showMappingModal && (
        <SupplierMappingModal
          clientId={clientId}
          serviceLabels={data.service_labels}
          onClose={() => setShowMappingModal(false)}
          onSaved={load}
        />
      )}
    </div>
  );
};

// Modal per gestione mapping fornitore → servizio
const SupplierMappingModal = ({ clientId, serviceLabels, onClose, onSaved }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/${clientId}/service-supplier-mappings`);
        setMappings(r.data.mappings || []);
      } catch (e) {
        toast.error("Errore caricamento mapping");
      } finally { setLoading(false); }
    })();
  }, [clientId]);

  const addRow = () => setMappings([...mappings, { supplier_keyword: "", service_type: "cleaning", allocation_pct: 100, note: "" }]);
  const removeRow = (i) => setMappings(mappings.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => {
    const next = [...mappings];
    next[i] = { ...next[i], [field]: val };
    setMappings(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/service-supplier-mappings`, { mappings });
      toast.success("Mapping salvato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Errore salvataggio mapping");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="supplier-mapping-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold">Mapping Fornitore → Servizio</h3>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="text-xs text-slate-500 mb-3">
            La <strong>keyword</strong> viene cercata (case-insensitive, ignora spazi/punteggiatura) nel nome del fornitore.
            La <strong>% Allocazione</strong> permette split multi-servizio (es. Pestnet 54% derat + 46% disinf su due righe separate).
            Usa il servizio "Personale (già in bilancio)" per escludere fornitori il cui costo è già dentro il Personale del bilancio (evita doppio conteggio).
          </div>
          {loading ? <div>Caricamento…</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Keyword</th>
                  <th className="p-2 text-left">Servizio</th>
                  <th className="p-2 text-right">% Alloc.</th>
                  <th className="p-2 text-left">Note</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, i) => (
                  <tr key={i} className="border-b" data-testid={`mapping-row-${i}`}>
                    <td className="p-1"><Input value={m.supplier_keyword} onChange={(e) => updateRow(i, "supplier_keyword", e.target.value)} className="h-8" placeholder="es. pestnet" /></td>
                    <td className="p-1">
                      <Select value={m.service_type} onValueChange={(v) => updateRow(i, "service_type", v)}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(serviceLabels).map(([k, lb]) => (
                            <SelectItem key={k} value={k}>{lb}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input type="number" min={0} max={100} step={1} value={m.allocation_pct} onChange={(e) => updateRow(i, "allocation_pct", parseFloat(e.target.value) || 0)} className="h-8 w-20 text-right" /></td>
                    <td className="p-1"><Input value={m.note || ""} onChange={(e) => updateRow(i, "note", e.target.value)} className="h-8" placeholder="Note" /></td>
                    <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`btn-remove-mapping-${i}`}>🗑</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Button size="sm" variant="outline" className="mt-3" onClick={addRow} data-testid="btn-add-mapping">+ Aggiungi riga</Button>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-mapping">{saving ? "Salvo…" : "Salva"}</Button>
        </div>
      </div>
    </div>
  );
};
